import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Clock,
  CheckCircle,
  TrendingUp,
  Calendar,
  Info,
  AlertTriangle,
  FileText,
  Route,
  Users,
  Circle
} from 'lucide-react';

export const RouteTrackerGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Route Tracker'
      description='Monitor active routes and track technician progress in real-time'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Route Tracker' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Overview Section */}
        <section className='mb-20' id='overview'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mb-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  What is the Route Tracker?
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  The Route Tracker is your daily status management tool for closing out completed
                  recall work. It shows you all routes by date with a calendar view, lets you update
                  the status of individual recalls (completed, vehicle not there, etc.), and track
                  which work has been finished. Think of it as your end-of-day checklist to mark
                  recalls as complete.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Use Route Tracker Section */}
        <section className='mb-20' id='why-use-route-tracker'>
          <h2 className='text-3xl font-bold mb-6'>Why Use Route Tracker?</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Route Tracker helps you manage recall completions and track your team's daily
            progress. Here's what it does for you:
          </p>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <TrendingUp className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Update Recall Statuses</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Mark recalls as completed, vehicle not there, incomplete, or other status. Keep
                  your system accurate and up-to-date.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Clock className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Calendar View</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Navigate by date with a visual calendar. See which days have pending status
                  updates with colored indicators - orange for pending, green for all complete.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Users className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Bulk Status Updates</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Select multiple recalls and update their status at once. Perfect for marking a
                  batch of completed work quickly.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Calendar className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Track Daily Progress</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  See daily statistics showing total routes, completed recalls, and pending updates.
                  Monitor progress bars for each technician's route completion.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How to Access Section */}
        <section className='mb-20' id='how-to-access-route-tracker'>
          <h2 className='text-3xl font-bold mb-6 flex items-center gap-3'>
            <MapPin className='h-8 w-8 text-primary' />
            How to Access Route Tracker
          </h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Follow these steps to access the Route Tracker dashboard:
          </p>

          <div className='space-y-6'>
            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                1
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Open the Sidebar</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Look at the left side of your screen for the main navigation menu.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                2
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Find the TRACK Section
                </h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Scroll down in the sidebar until you see the "TRACK" heading. This section is for
                  monitoring your operations.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                3
              </div>
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Click Route Tracker</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Click on "Route Tracker" under the TRACK section. The page will load showing your
                  routes.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-muted border-l-4 border-border p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Can't see Route Tracker?
                </h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  If you don't see the Route Tracker option, your role may not have access to it.
                  Contact your system administrator or supervisor to request access if you need it
                  for your job.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding the Interface Section */}
        <section className='mb-20' id='understanding-the-interface'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Interface</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Route Tracker has a two-column layout:
          </p>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Left Column - Calendar</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>• Navigate between months with arrow buttons</li>
                  <li>• Click any day to see routes for that date</li>
                  <li>• Orange dots = pending status updates</li>
                  <li>• Green dots = all statuses updated</li>
                  <li>• Today's date has a blue border</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Right Column - Routes</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>• One card per technician route</li>
                  <li>• Progress bar shows completion percentage</li>
                  <li>• Click route card to expand details</li>
                  <li>• Individual recall status updates</li>
                  <li>• Bulk actions for multiple recalls</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  Daily Summary Panel
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Below the calendar, you'll see a summary for the selected date showing total
                  routes, total recalls, completed count, and any pending updates that need
                  attention.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Recall Status Options Section */}
        <section className='mb-20' id='recall-status-options'>
          <h2 className='text-3xl font-bold mb-6'>Recall Status Options</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            When updating individual recalls, you can choose from these status options:
          </p>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Badge className='bg-green-600 text-white mt-1 text-sm px-3 py-1'>
                    Completed
                  </Badge>
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Service Completed Successfully</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      The recall work was completed successfully. Vehicle is fixed and ready to go.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Badge className='bg-orange-600 text-white mt-1 text-sm px-3 py-1'>
                    Vehicle Not There
                  </Badge>
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Customer Not Available</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Technician arrived but vehicle wasn't there, customer not home, or appointment
                      couldn't proceed as scheduled.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Badge className='bg-blue-600 text-white mt-1 text-sm px-3 py-1'>
                    Incomplete
                  </Badge>
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Work in Progress</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Work started but couldn't be finished today. May need additional time, parts,
                      or follow-up appointment.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Badge className='bg-gray-600 text-white mt-1 text-sm px-3 py-1'>
                    Other Reason
                  </Badge>
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Custom Situation</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Other reason for not completing the recall. You can select from common reasons
                      or type a custom explanation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Keep Records Accurate
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Accurate status updates help with customer service, warranty tracking, and
                  ensuring no recalls get lost in the system. Update statuses at the end of each
                  day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Working with Route Cards Section */}
        <section className='mb-20' id='working-with-route-cards'>
          <h2 className='text-3xl font-bold mb-6'>Working with Route Cards</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Each route shows up as a card for each technician. Here's how to work with them:
          </p>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Route Card Header</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>
                    • <strong>Technician name</strong> and initials in circle
                  </li>
                  <li>
                    • <strong>Total recalls</strong> assigned to this route
                  </li>
                  <li>
                    • <strong>Number of dealers</strong> visited
                  </li>
                  <li>
                    • <strong>"Pending" badge</strong> if recalls need status updates
                  </li>
                  <li>
                    • <strong>Progress bar</strong> showing completion percentage
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Expanding Route Details</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>
                    • Click the <strong>chevron arrow</strong> to expand/collapse
                  </li>
                  <li>
                    • <strong>Pending recalls</strong> show first (need status updates)
                  </li>
                  <li>
                    • <strong>Completed recalls</strong> show below
                  </li>
                  <li>
                    • Recalls grouped by <strong>campaign number</strong>
                  </li>
                  <li>• Each recall shows vehicle details and dealer location</li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Bulk Selection</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>
                    • <strong>Green square button</strong> selects all pending recalls in route
                  </li>
                  <li>
                    • <strong>Individual checkboxes</strong> for each recall
                  </li>
                  <li>• Selected recalls highlighted with colored background</li>
                  <li>• Use bulk actions to update multiple recalls at once</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  Updating Individual Recalls
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Each recall has dropdowns to set its status and choose which technician actually
                  performed the work. You can also add custom reasons for "Other" status.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Saving Changes Section */}
        <section className='mb-20' id='saving-changes'>
          <h2 className='text-3xl font-bold mb-6'>Saving Your Changes</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            After updating recall statuses, you need to save your changes:
          </p>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Save Button</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>
                    • <strong>"Save Changes"</strong> button appears at top when you have unsaved
                    changes
                  </li>
                  <li>
                    • <strong>Orange reminder banner</strong> shows at bottom of screen
                  </li>
                  <li>• Click "Save Changes" to commit all status updates to the database</li>
                  <li>
                    • <strong>Refresh button</strong> reloads data if needed
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Future Date Protection</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                  <li>
                    • <strong>Cannot update</strong> recalls scheduled for future dates
                  </li>
                  <li>• System prevents accidental updates to future work</li>
                  <li>• Error message explains why update was blocked</li>
                  <li>• Only update recalls for today or past dates</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className='bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-amber-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2'>
                  Don't Forget to Save!
                </h4>
                <p className='text-sm leading-6 text-amber-800 dark:text-amber-200'>
                  Status updates are not permanent until you click "Save Changes". The system
                  reminds you with visual indicators, but make sure to save before leaving the page.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions Section */}
        <section className='mb-20' id='common-questions'>
          <h2 className='text-3xl font-bold mb-10'>Common Questions</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Why can't I update the status of some recalls?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                The system prevents updates to recalls scheduled for future dates. You can only
                update recalls for today or past dates. This prevents accidental status changes for
                work that hasn't happened yet.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                What happens if I don't save my changes?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Status updates are only saved in your browser until you click "Save Changes". If you
                leave the page without saving, all updates will be lost. The system shows reminders
                to help you remember.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                How do I know which recalls need status updates?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Look for orange dots on calendar days (pending updates), red "Pending" badges on
                route cards, and recalls that appear in the "Pending" section when you expand a
                route. The daily summary also shows how many recalls need updates.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Can I change which technician performed the work?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Yes! Each recall has a "Serviced By" dropdown where you can select the technician
                who actually performed the work. This is helpful if routes were reassigned or
                technicians helped each other.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                What's the difference between bulk updates and individual updates?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Individual updates let you set different statuses and add custom reasons for each
                recall. Bulk updates apply the same status to multiple selected recalls at once,
                which is faster for marking many completed recalls.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Reference Section */}
        <section className='mb-20' id='quick-reference'>
          <h2 className='text-3xl font-bold mb-10'>Quick Reference</h2>

          <div className='grid gap-6 md:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <div className='flex items-center gap-3'>
                  <Route className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg'>Status Colors</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      Service completed successfully
                    </span>
                    <code className='px-2 py-1 text-xs bg-green-100 text-green-800 rounded'>
                      Green
                    </code>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      Vehicle not there/available
                    </span>
                    <code className='px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded'>
                      Orange
                    </code>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>
                      Work incomplete/in progress
                    </span>
                    <code className='px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded'>
                      Blue
                    </code>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>Other custom reason</span>
                    <code className='px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded'>
                      Gray
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader>
                <div className='flex items-center gap-3'>
                  <Calendar className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg'>Calendar Indicators</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>All statuses updated</span>
                    <div className='flex items-center gap-2'>
                      <Circle className='h-3 w-3 fill-green-500 text-green-500' />
                      <code className='px-2 py-1 text-xs bg-muted rounded'>Green Dot</code>
                    </div>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>Pending status updates</span>
                    <div className='flex items-center gap-2'>
                      <Circle className='h-3 w-3 fill-orange-500 text-orange-500' />
                      <code className='px-2 py-1 text-xs bg-muted rounded'>Orange Dot</code>
                    </div>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>Today's date</span>
                    <code className='px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded'>
                      Blue Border
                    </code>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-muted-foreground'>No routes scheduled</span>
                    <code className='px-2 py-1 text-xs bg-muted rounded'>No Indicator</code>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className='mb-20' id='next-steps'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Now that you understand Route Tracker, you might want to learn about:
          </p>

          <div className='grid gap-6 sm:grid-cols-3'>
            <Link to='/docs/features/manual-routing' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Creating Routes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    How to assign recalls to technicians and create new routes
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to='/docs/features/technicians' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Managing Technicians
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    How to add, edit, and manage your technician team
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to='/docs/features/recall-list' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Recall List
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    How to view and search all recalls in your system
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>

        {/* Help Section */}
        <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
          <div className='flex gap-4'>
            <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
            <div>
              <h4 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                Need Help?
              </h4>
              <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                If you are having trouble with Route Tracker or have questions not covered in this
                guide, contact your supervisor or reach out to{' '}
                <a
                  href='mailto:contact@kebi.ai'
                  className='underline hover:no-underline font-semibold'
                >
                  contact@kebi.ai
                </a>{' '}
                for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
};
